import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Autocomplete,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../api/api';

const initialAlergiaFormData = {
  id_alergia: '',
  es_padecer: false,
  comentario: '',
  gravedad: '',
  reaccion: '',
};

// Función para obtener el color basado en la gravedad
const getSeverityColor = (gravedad) => {
    switch (gravedad?.toLowerCase()) {
      case 'leve':
        return '#4caf50'; // Verde
      case 'moderada':
        return '#ff9800'; // Naranja
      case 'grave':
        return '#f44336'; // Rojo
      case 'mortal':
        return '#000000'; // Rojo oscuro
      default:
        return '#e0e0e0'; // Gris por defecto (N/A)
    }
  };

const PacienteAlergias = ({ pacienteId, alergias, setAlergias, alergiasCatalog, showSnackbar, disabled }) => {
  const [openAlergiaModal, setOpenAlergiaModal] = useState(false);
  const [selectedAlergia, setSelectedAlergia] = useState(null);
  const [alergiaFormData, setAlergiaFormData] = useState(initialAlergiaFormData);
  const [loading, setLoading] = useState(false);

  // Manejador de cambios en el formulario del modal de alergias
  const handleAlergiaChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setAlergiaFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  // Abre el modal para crear o editar
  const handleOpenAlergiaModal = useCallback((alergia = null) => {
    setSelectedAlergia(alergia);
    setAlergiaFormData(alergia ? {
      id_alergia: alergia.id_alergia || '',
      es_padecer: alergia.es_padecer || false,
      comentario: alergia.comentario || '',
      gravedad: alergia.gravedad || '',
      reaccion: alergia.reaccion || '',
    } : initialAlergiaFormData);
    setOpenAlergiaModal(true);
  }, []);

  // Guarda la alergia (Crear o Editar)
  const handleSaveAlergia = async () => {
    // Validaciones
    if (!alergiaFormData.id_alergia) {
      showSnackbar('Debe seleccionar una alergia', true);
      return;
    }
    if (!alergiaFormData.gravedad) {
      showSnackbar('La gravedad es obligatoria', true);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id_paciente: parseInt(pacienteId),
        id_alergia: parseInt(alergiaFormData.id_alergia),
        es_padecer: alergiaFormData.es_padecer,
        comentario: alergiaFormData.comentario || null,
        gravedad: alergiaFormData.gravedad,
        reaccion: alergiaFormData.reaccion || null,
      };

      let response;
      if (selectedAlergia) {
        // Actualizar
        response = await api.put(`/alergias-pacientes/${selectedAlergia.id_alergia_paciente}`, payload);
      } else {
        // Crear
        response = await api.post('/alergias-pacientes/', payload);
      }
      
      const updatedAlergia = response.data;
      
      // Actualizar el estado de alergias en el componente padre a través de setAlergias
      setAlergias(prevAlergias => 
        selectedAlergia
          ? prevAlergias.map(a => (a.id_alergia_paciente === updatedAlergia.id_alergia_paciente ? updatedAlergia : a))
          : [...prevAlergias, updatedAlergia]
      );
      
      showSnackbar(`Alergia ${selectedAlergia ? 'actualizada' : 'creada'} exitosamente`);
      setOpenAlergiaModal(false);
    } catch (err) {
      const msg = 'Error al guardar alergia: ' + (err.response?.data?.detail || err.message);
      showSnackbar(msg, true);
    } finally {
      setLoading(false);
    }
  };

  // Elimina la alergia
  const handleDeleteAlergia = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/alergias-pacientes/${id}`);
      // Actualizar el estado de alergias
      setAlergias(prevAlergias => prevAlergias.filter(a => a.id_alergia_paciente !== id));
      showSnackbar('Alergia eliminada exitosamente');
    } catch (err) {
      const msg = 'Error al eliminar alergia: ' + (err.response?.data?.detail || err.message);
      showSnackbar(msg, true);
    } finally {
      setLoading(false);
    }
  };

  // Obtener la descripción de la alergia por ID
const getAlergiaDescripcion = (id_alergia_param) => {
  // Pista 1: Ver si el catálogo tiene datos cuando se llama a la función
  console.log("Catálogo actual:", alergiasCatalog);
  console.log("Buscando ID:", id_alergia_param, "Tipo:", typeof id_alergia_param);

  if (!id_alergia_param || alergiasCatalog.length === 0) {
    console.warn("Búsqueda abortada: ID nulo o catálogo vacío");
    return 'Cargando...';
  }

  // Pista 2: Ver la comparación uno a uno
  const encontrada = alergiasCatalog.find((a) => {
    const coinciden = String(a.id_alergia) === String(id_alergia_param);
    if (coinciden) console.log("¡Coincidencia encontrada!", a);
    return coinciden;
  });

  if (!encontrada) {
    console.error(`No se encontró el ID ${id_alergia_param} en el catálogo de ${alergiasCatalog.length} elementos`);
  }

  return encontrada ? encontrada.descripcion : 'No encontrada';
};
  
  // Buscar el objeto alergia para el Autocomplete del modal
  const selectedAlergiaCatalogItem = 
    alergiasCatalog.find(a => a.id_alergia === alergiaFormData.id_alergia) || null;

  // NUEVO: Lista de alergias disponibles para seleccionar
  const availableAlergias = selectedAlergia 
  ? alergiasCatalog // Si editamos, mostramos todo para no perder la descripción
  : alergiasCatalog.filter(a => a.habilitada); // Si es nueva, solo las activas

  return (
    <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ccc' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Registro de Alergias 🤧
      </Typography>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1 }}>Cargando...</Typography>
        </Box>
      )}

      {/* Botón para abrir el modal de nueva alergia */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenAlergiaModal()}
        sx={{ mb: 2 }}
        disabled={disabled}
      >
        Nueva Alergia
      </Button>

      {/* --- Cards de Alergias (Reemplazo de la Tabla) --- */}
      {alergias.length === 0 ? (
        <Alert severity="info">No hay alergias registradas para este paciente.</Alert>
      ) : (
        <Grid container spacing={2}>
          {alergias.map((alergia) => {
            const severityColor = getSeverityColor(alergia.gravedad);
            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={alergia.id_alergia_paciente}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                
                {/* Franja de color para indicar la gravedad */}
                <Box sx={{ height: 10, bgcolor: severityColor }} />

                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Chip para mostrar la gravedad sin el texto "Gravedad:" */}
                     <Chip 
                       label={alergia.gravedad || 'N/A'} 
                       size="small" 
                       sx={{ 
                         bgcolor: severityColor, 
                         color: '#fff', // Texto blanco para contraste
                         fontWeight: 'bold'
                       }} 
                     />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    
                     <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {/* USAR alergia.id_alergia AQUÍ */}
          {getAlergiaDescripcion(alergia.id_alergia)} 
        </Typography>
                  </Box>              
                                    
                  {(alergia.reaccion || alergia.comentario) && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #9c9c9cff' }}>
                      {alergia.reaccion && (
                        <Typography variant="caption" display="block" color="#000000">
                          *Reacción:* {alergia.reaccion}
                        </Typography>
                      )}
                      
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #9c9c9cff'} }></Box>
                      {alergia.comentario && (
                        <Typography variant="caption" display="block" color="#000000">
                          *Comentario:* {alergia.comentario}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
                  <IconButton 
                    onClick={() => handleOpenAlergiaModal(alergia)} 
                    color="primary" 
                    size="small"
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDeleteAlergia(alergia.id_alergia_paciente)} 
                    color="error" 
                    size="small"
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          )})}
        </Grid>
      )}

      {/* --- Modal para crear/editar alergia --- */}
      <Dialog 
        open={openAlergiaModal} 
        onClose={() => setOpenAlergiaModal(false)}
        aria-labelledby="alergia-dialog-title"
      >
        <DialogTitle id="alergia-dialog-title">
          {selectedAlergia ? 'Editar Alergia del Paciente' : 'Registrar Nueva Alergia'}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            // CAMBIO CLAVE: Usamos la lista filtrada 'availableAlergias'
            options={availableAlergias}
            getOptionLabel={(option) => option.descripcion || ''}
            getOptionKey={(option) => option.id_alergia} 
            value={selectedAlergiaCatalogItem}
            onChange={(e, newValue) => setAlergiaFormData({ 
              ...alergiaFormData, 
              id_alergia: newValue ? newValue.id_alergia : '' 
            })}
            renderInput={(params) => (
              <TextField {...params} label="Alergia" fullWidth required sx={{ mt: 2 }} />
            )}
            disabled={!!selectedAlergia} // Deshabilitar si se está editando
            isOptionEqualToValue={(option, value) => option.id_alergia === value.id_alergia}
          />
          <TextField
            select
            label="Gravedad"
            fullWidth
            name="gravedad"
            value={alergiaFormData.gravedad || ''}
            onChange={handleAlergiaChange}
            required
            sx={{ mt: 2 }}
          >
            <MenuItem value="">Seleccionar</MenuItem>
            <MenuItem value="Leve">Leve</MenuItem>
            <MenuItem value="Moderada">Moderada</MenuItem>
            <MenuItem value="Grave">Grave</MenuItem>
            <MenuItem value="Mortal">Mortal</MenuItem>
          </TextField>
          <TextField
            label="Reacción"
            fullWidth
            name="reaccion"
            value={alergiaFormData.reaccion || ''}
            onChange={handleAlergiaChange}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Comentario"
            fullWidth
            name="comentario"
            value={alergiaFormData.comentario || ''}
            onChange={handleAlergiaChange}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <TextField
              type="checkbox"
              name="es_padecer"
              checked={alergiaFormData.es_padecer}
              onChange={handleAlergiaChange}
              label="Es Padecer"
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 1 }}
            />
            <Typography>Es Padecer (Alergia activa/diagnosticada)</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlergiaModal(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveAlergia}
            variant="contained"
            disabled={loading || disabled || !alergiaFormData.id_alergia || !alergiaFormData.gravedad}
          >
            Guardar {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PacienteAlergias;